import React, { useEffect, useMemo, useState, useCallback } from "react";
import { DataGrid } from "@material-ui/data-grid";
import { Button } from "@material-ui/core";
import { AiOutlineArrowRight } from "react-icons/ai";
import { RxCross1 } from "react-icons/rx";
import { Link } from "react-router-dom";
import axios from "axios";
import { server } from "../../server";
import DashboardTableScroll from "../shared/DashboardTableScroll";
import { SimpleLoader } from "../Layout/Loader";
import { useDispatch, useSelector } from "react-redux";
import { getAllOrdersOfShop, getAllOrdersOfUser } from "../../redux/actions/order";

const formatCurrency = (value, currency = "GBP") => {
  const numeric = Number(value || 0);
  return new Intl.NumberFormat("en-GB", {
    style: "currency",
    currency: String(currency || "GBP").toUpperCase(),
  }).format(numeric);
};

const formatDirection = (direction) => {
  if (direction === "in") return "In";
  if (direction === "out") return "Out";
  return "Neutral";
};

const normalizeStatus = (status) => {
  if (!status) return "N/A";
  return String(status)
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
};

const formatDateTime = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleString();
};

const formatDateOnly = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleDateString();
};

const promotionStatusLabel = (status) => {
  const key = String(status || "").toLowerCase();
  const labels = {
    pending: "Pending approval",
    active: "Active",
    expired: "Expired",
    rejected: "Rejected",
    draft: "Draft",
    payment_due: "Payment due",
  };
  return labels[key] || normalizeStatus(status);
};

const promotionStatusClass = (status) => {
  const key = String(status || "").toLowerCase();
  const styles = {
    pending: "bg-amber-100 text-amber-800",
    active: "bg-emerald-100 text-emerald-800",
    expired: "bg-slate-200 text-slate-700",
    rejected: "bg-rose-100 text-rose-800",
    draft: "bg-slate-100 text-slate-600",
    payment_due: "bg-rose-100 text-rose-800",
  };
  return styles[key] || "bg-slate-100 text-slate-700";
};

const paymentPlanSummary = (details) => {
  if (!details) return "—";
  const plan = String(details.paymentPlan || "").toLowerCase();
  const weeks = Number(details.durationWeeks || 1);
  if (plan === "weekly") {
    const paid = Number(details.periodsPaid || 1);
    const remaining = Number(details.paymentsRemaining ?? Math.max(0, weeks - paid));
    return `Weekly — ${paid} of ${weeks} payment${weeks === 1 ? "" : "s"}${remaining > 0 ? ` (${remaining} remaining)` : " (complete)"}`;
  }
  if (plan === "upfront") {
    return `Upfront — ${weeks} week${weeks === 1 ? "" : "s"}`;
  }
  const cycle = details.billingCycle === "monthly" ? "monthly" : "weekly";
  return `Subscription (${cycle})`;
};

const RelatedPromotionDetails = ({ details, category }) => {
  if (!details) {
    const label = category === "service" ? "service" : "event";
    return (
      <p className="text-sm text-slate-500">
        {label === "service" ? "Service" : "Event"} details are not available. Refresh the
        page to try again.
      </p>
    );
  }

  const isService = details.entityType === "service" || category === "service";
  const label = isService ? "Service" : "Event";
  const statusKey = String(details.status || "").toLowerCase();
  const endDate =
    statusKey === "expired" && details.expiredAt
      ? details.expiredAt
      : details.approvedEnd || details.expiresAt;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h4 className="text-sm font-semibold text-slate-900">
            Related {label.toLowerCase()}
          </h4>
          <p className="mt-1 text-base font-semibold text-slate-900">
            {details.name || "Untitled"}
          </p>
        </div>
        <span
          className={`inline-flex rounded-full px-3 py-1 text-xs font-semibold ${promotionStatusClass(details.status)}`}
        >
          {promotionStatusLabel(details.status)}
        </span>
      </div>

      {details.description ? (
        <p className="text-sm text-slate-600 line-clamp-3">{details.description}</p>
      ) : null}

      <dl className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <div className="space-y-1">
          <dt className="text-xs font-semibold uppercase tracking-wide text-slate-500">
            Payment plan
          </dt>
          <dd className="text-sm font-semibold text-slate-900">
            {paymentPlanSummary(details)}
          </dd>
        </div>
        <div className="space-y-1">
          <dt className="text-xs font-semibold uppercase tracking-wide text-slate-500">
            Total booked
          </dt>
          <dd className="text-sm font-semibold text-slate-900">
            {formatCurrency(details.totalAmount, details.currency)}
          </dd>
        </div>
        {String(details.paymentPlan || "").toLowerCase() === "weekly" &&
        Number(details.weeklyRate) > 0 ? (
          <div className="space-y-1">
            <dt className="text-xs font-semibold uppercase tracking-wide text-slate-500">
              Weekly rate
            </dt>
            <dd className="text-sm font-semibold text-slate-900">
              {formatCurrency(details.weeklyRate, details.currency)}
            </dd>
          </div>
        ) : null}
        <div className="space-y-1">
          <dt className="text-xs font-semibold uppercase tracking-wide text-slate-500">
            Payment method
          </dt>
          <dd className="text-sm font-semibold text-slate-900">
            {normalizeStatus(details.paymentMethod)}
          </dd>
        </div>
        <div className="space-y-1">
          <dt className="text-xs font-semibold uppercase tracking-wide text-slate-500">
            Preferred start
          </dt>
          <dd className="text-sm font-semibold text-slate-900">
            {formatDateOnly(details.preferredStart)}
          </dd>
        </div>
        <div className="space-y-1">
          <dt className="text-xs font-semibold uppercase tracking-wide text-slate-500">
            {details.approvedStart ? "Approved start" : "Start (scheduled)"}
          </dt>
          <dd className="text-sm font-semibold text-slate-900">
            {formatDateOnly(details.approvedStart || details.startDate)}
          </dd>
        </div>
        <div className="space-y-1">
          <dt className="text-xs font-semibold uppercase tracking-wide text-slate-500">
            {statusKey === "expired" ? "Expired on" : "Runs until"}
          </dt>
          <dd className="text-sm font-semibold text-slate-900">
            {formatDateOnly(endDate)}
          </dd>
        </div>
        {String(details.paymentPlan || "").toLowerCase() === "weekly" &&
        details.isSubscriptionActive &&
        details.nextBillingDate ? (
          <div className="space-y-1">
            <dt className="text-xs font-semibold uppercase tracking-wide text-slate-500">
              Next payment
            </dt>
            <dd className="text-sm font-semibold text-slate-900">
              {formatDateOnly(details.nextBillingDate)}
            </dd>
          </div>
        ) : null}
        {details.subscriptionCancelledAt ? (
          <div className="space-y-1 sm:col-span-2">
            <dt className="text-xs font-semibold uppercase tracking-wide text-slate-500">
              Subscription ended
            </dt>
            <dd className="text-sm font-semibold text-slate-900">
              {formatDateTime(details.subscriptionCancelledAt)}
            </dd>
          </div>
        ) : null}
      </dl>

      {statusKey === "rejected" && details.rejectionReason ? (
        <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-800">
          <p className="font-semibold">Rejection reason</p>
          <p className="mt-1">{details.rejectionReason}</p>
        </div>
      ) : null}

      {statusKey === "payment_due" ? (
        <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-900">
          <p className="font-semibold">Payment required</p>
          <p className="mt-1">
            This {label.toLowerCase()} is hidden until the outstanding weekly payment is received.
            {details.subscriptionFailureReason
              ? ` ${details.subscriptionFailureReason}`
              : ""}
          </p>
        </div>
      ) : null}

      {statusKey === "expired" ? (
        <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700">
          <p className="font-semibold text-slate-900">No longer active</p>
          <p className="mt-1">
            This {label.toLowerCase()} has expired
            {endDate ? ` as of ${formatDateOnly(endDate)}` : ""}.
            {details.subscriptionFailureReason
              ? ` Last billing issue: ${details.subscriptionFailureReason}`
              : ""}
          </p>
        </div>
      ) : null}

      {details.lastBillingStatus === "failed" &&
      statusKey !== "expired" &&
      statusKey !== "payment_due" ? (
        <div className="rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
          <p className="font-semibold">Billing issue</p>
          <p className="mt-1">
            {details.subscriptionFailureReason || "The last payment attempt failed."}
          </p>
        </div>
      ) : null}
    </div>
  );
};

const parseOrderIdFromLink = (link) => {
  if (!link || typeof link !== "string") return null;
  const match = link.match(/\/profile\/(seller-)?order\/([^/]+)/);
  if (!match) return null;
  return { isSellerOrder: Boolean(match[1]), orderId: match[2] };
};

const AllTransactions = () => {
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.user);
  const { orders, shopOrders } = useSelector((state) => state.order || {});

  const [loading, setLoading] = useState(true);
  const [transactions, setTransactions] = useState([]);
  const [typeFilter, setTypeFilter] = useState("all");
  const [selectedTx, setSelectedTx] = useState(null);

  useEffect(() => {
    const loadTransactions = async () => {
      try {
        const { data } = await axios.get(`${server}/user/my-transactions`, {
          withCredentials: true,
        });
        setTransactions(Array.isArray(data?.transactions) ? data.transactions : []);
      } catch (error) {
        setTransactions([]);
      } finally {
        setLoading(false);
      }
    };

    loadTransactions();
  }, []);

  const uniqueTransactions = useMemo(() => {
    const seen = new Set();
    const result = [];

    for (const tx of transactions || []) {
      const createdAtMs = tx?.createdAt ? new Date(tx.createdAt).getTime() : 0;
      const roundedMs = createdAtMs
        ? createdAtMs - (createdAtMs % (60 * 1000)) // minute precision
        : 0;

      const amountRaw = Number(tx?.amount || 0);
      const amount = Number.isFinite(amountRaw) ? amountRaw : 0;
      const currency = tx?.currency || "GBP";
      const reference = tx?.reference || "";
      const link = tx?.link || "";
      const category = tx?.category || tx?.source || "";
      const direction = tx?.direction || "";
      const title = tx?.title || "";

      const sig = [
        category,
        direction,
        amount.toFixed(2),
        currency,
        reference,
        link,
        title,
        roundedMs,
      ].join("|");

      if (seen.has(sig)) continue;
      seen.add(sig);
      result.push(tx);
    }

    return result;
  }, [transactions]);

  const filteredTransactions = useMemo(() => {
    if (typeFilter === "all") return uniqueTransactions;
    return (uniqueTransactions || []).filter(
      (tx) => String(tx?.category || "").toLowerCase() === typeFilter
    );
  }, [typeFilter, uniqueTransactions]);

  const sortedTransactions = useMemo(() => {
    return [...(filteredTransactions || [])].sort(
      (a, b) => new Date(b?.createdAt || 0).getTime() - new Date(a?.createdAt || 0).getTime()
    );
  }, [filteredTransactions]);

  const orderMeta = useMemo(() => {
    if (!selectedTx || selectedTx.category !== "order") return null;
    return parseOrderIdFromLink(selectedTx.link);
  }, [selectedTx]);

  const relatedOrder = useMemo(() => {
    if (!orderMeta?.orderId) return null;
    const list = orderMeta.isSellerOrder ? shopOrders : orders;
    const safeList = Array.isArray(list) ? list : [];
    return (
      safeList.find((o) => String(o?._id) === String(orderMeta.orderId)) ||
      null
    );
  }, [orderMeta, orders, shopOrders]);

  // Lazy-load order products only if we need them for the popup.
  useEffect(() => {
    if (!selectedTx || selectedTx.category !== "order") return;
    if (!user?._id) return;
    if (!orderMeta?.orderId) return;

    if (orderMeta.isSellerOrder) {
      if (shopOrders === undefined) {
        dispatch(getAllOrdersOfShop(user._id));
      }
    } else if (orders === undefined) {
      dispatch(getAllOrdersOfUser(user._id));
    }
  }, [
    dispatch,
    orderMeta?.isSellerOrder,
    orderMeta?.orderId,
    orders,
    selectedTx,
    shopOrders,
    user?._id,
  ]);

  const columns = useMemo(
    () => [
      { field: "id", headerName: "ID", width: 0, sortable: true },
      { field: "title", headerName: "Transaction", minWidth: 210, flex: 1 },
      {
        field: "category",
        headerName: "Type",
        minWidth: 120,
        flex: 0.6,
        valueFormatter: (params) => normalizeStatus(params.value),
      },
      {
        field: "direction",
        headerName: "Flow",
        minWidth: 100,
        flex: 0.4,
        valueFormatter: (params) => formatDirection(params.value),
      },
      {
        field: "amount",
        headerName: "Amount",
        minWidth: 130,
        flex: 0.6,
        valueFormatter: (params) =>
          formatCurrency(params.value, params.row.currency),
      },
      {
        field: "status",
        headerName: "Status",
        minWidth: 140,
        flex: 0.6,
        valueFormatter: (params) => normalizeStatus(params.value),
      },
      {
        field: "createdAt",
        headerName: "Date",
        minWidth: 140,
        flex: 0.6,
        valueFormatter: (params) =>
          params.value ? new Date(params.value).toLocaleDateString() : "",
      },
      {
        field: "actions",
        headerName: "",
        minWidth: 100,
        flex: 0.4,
        sortable: false,
        renderCell: (params) =>
          params.row.link ? (
            <Link
              to={params.row.link}
              onClick={(e) => e.stopPropagation()}
              onMouseDown={(e) => e.stopPropagation()}
            >
              <Button>
                <AiOutlineArrowRight size={18} />
              </Button>
            </Link>
          ) : null,
      },
    ],
    []
  );

  const rows = useMemo(
    () =>
      (sortedTransactions || []).map((tx, index) => ({
        id: tx.id || `${tx.category || "tx"}-${tx.createdAt || index}-${index}`,
        title: tx.title || "Transaction",
        category: tx.category || "other",
        direction: tx.direction || "neutral",
        amount: Number(tx.amount || 0),
        currency: tx.currency || "GBP",
        status: tx.status || "N/A",
        createdAt: tx.createdAt,
        link: tx.link || "",
        __rawTx: tx,
      })),
    [sortedTransactions]
  );

  const handleRowClick = useCallback((params) => {
    const tx = params?.row?.__rawTx || params?.row;
    setSelectedTx(tx);
  }, []);

  if (loading) {
    return (
      <section className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm sm:p-8">
        <SimpleLoader />
      </section>
    );
  }

  return (
    <section className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm sm:p-8">
      <header className="mb-6">
        <h2 className="text-lg font-semibold text-slate-900">Transactions</h2>
        <p className="text-sm text-slate-500">
          All account activity in one place: orders, events/services, wallet top-ups, and withdrawals.
        </p>
      </header>

      <div className="mb-5 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-2 text-sm font-medium text-slate-700">
          Filter
        </div>
        <label className="flex items-center gap-2">
          <span className="text-sm font-medium text-slate-700">Type</span>
          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            className="w-full sm:w-56 rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-800 shadow-sm focus:border-[#38513b] focus:outline-none focus:ring-2 focus:ring-[#38513b]/20"
          >
            <option value="all">All</option>
            <option value="order">Orders</option>
            <option value="service">Services</option>
            <option value="event">Events</option>
            <option value="wallet">Wallet</option>
            <option value="withdrawal">Withdrawals</option>
          </select>
        </label>
      </div>

      <div className="overflow-hidden rounded-2xl border border-slate-200">
        <DashboardTableScroll>
          <div className="min-w-[760px]">
            <DataGrid
              rows={rows}
              columns={columns}
              pageSize={10}
              disableSelectionOnClick
              autoHeight
              initialState={{
                columns: { columnVisibilityModel: { id: false } },
                sorting: { sortModel: [{ field: "createdAt", sort: "desc" }] },
              }}
              onRowClick={handleRowClick}
            />
          </div>
        </DashboardTableScroll>
      </div>

      {selectedTx && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 px-4 py-10 backdrop-blur-sm"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) setSelectedTx(null);
          }}
        >
          <div className="w-full max-w-2xl overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-xl">
            <div className="flex items-center justify-between border-b border-slate-200 px-6 py-4">
              <div className="min-w-0">
                <h3 className="truncate text-lg font-semibold text-slate-900">
                  {selectedTx?.title || "Transaction"}
                </h3>
                <p className="mt-1 text-sm text-slate-500">
                  {selectedTx?.category ? normalizeStatus(selectedTx.category) : "N/A"}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setSelectedTx(null)}
                className="rounded-full border border-transparent p-2 text-slate-500 transition hover:border-slate-200 hover:bg-slate-100 hover:text-slate-700"
                aria-label="Close"
              >
                <RxCross1 size={22} />
              </button>
            </div>

            <div className="max-h-[80vh] overflow-y-auto px-6 py-6 space-y-6">
              <dl className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <div className="space-y-1">
                  <dt className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                    Flow
                  </dt>
                  <dd className="text-sm font-semibold text-slate-900">
                    {formatDirection(selectedTx?.direction)}
                  </dd>
                </div>
                <div className="space-y-1">
                  <dt className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                    Status
                  </dt>
                  <dd className="text-sm font-semibold text-slate-900">
                    {normalizeStatus(selectedTx?.status)}
                  </dd>
                </div>
                <div className="space-y-1">
                  <dt className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                    Amount
                  </dt>
                  <dd className="text-sm font-semibold text-slate-900">
                    {formatCurrency(selectedTx?.amount, selectedTx?.currency)}
                  </dd>
                </div>
                <div className="space-y-1">
                  <dt className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                    Date
                  </dt>
                  <dd className="text-sm font-semibold text-slate-900">
                    {selectedTx?.createdAt
                      ? new Date(selectedTx.createdAt).toLocaleString()
                      : "—"}
                  </dd>
                </div>
                {selectedTx?.reference ? (
                  <div className="space-y-1 sm:col-span-2">
                    <dt className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                      Reference
                    </dt>
                    <dd className="text-sm font-semibold text-slate-900 break-all">
                      {selectedTx.reference}
                    </dd>
                  </div>
                ) : null}
              </dl>

              <div className="flex flex-wrap gap-3">
                {selectedTx?.link ? (
                  <Link
                    to={selectedTx.link}
                    className="inline-flex h-11 items-center justify-center rounded-xl bg-[#38513b] px-6 text-sm font-semibold text-white shadow-sm transition hover:bg-[#2f4232]"
                    onClick={(e) => e.stopPropagation()}
                  >
                    Open details
                  </Link>
                ) : null}

                {selectedTx?.category === "order" && selectedTx?.link ? (
                  <Link
                    to={selectedTx.link}
                    className="inline-flex h-11 items-center justify-center rounded-xl border border-slate-200 bg-white px-6 text-sm font-semibold text-slate-700 shadow-sm transition hover:border-[#38513b] hover:text-[#38513b]"
                    onClick={(e) => e.stopPropagation()}
                  >
                    View order
                  </Link>
                ) : null}
              </div>

              {selectedTx?.category === "order" && (
                <div className="space-y-3">
                  <h4 className="text-sm font-semibold text-slate-900">
                    Products in this order
                  </h4>

                  {relatedOrder ? (
                    Array.isArray(relatedOrder.cart) && relatedOrder.cart.length ? (
                      <div className="space-y-2">
                        {relatedOrder.cart.map((item) => {
                          const productId = item?._id;
                          const productName = item?.name || productId;
                          const qty = item?.qty ?? item?.quantity ?? 1;
                          return (
                            <div
                              key={`${productId}-${productName}`}
                              className="flex items-center justify-between rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3"
                            >
                              <div className="min-w-0">
                                {productId ? (
                                  <Link
                                    to={`/product/${productId}`}
                                    className="truncate block text-sm font-semibold text-blue-600 hover:underline"
                                  >
                                    {productName || "Product"}
                                  </Link>
                                ) : (
                                  <p className="text-sm font-semibold text-slate-700">
                                    {productName || "Product"}
                                  </p>
                                )}
                                {item?.variant ? (
                                  <p className="mt-0.5 text-xs text-slate-500">
                                    Variant: {item.variant}
                                  </p>
                                ) : null}
                              </div>
                              <span className="ml-3 text-sm font-semibold text-slate-900">
                                Qty {qty}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <p className="text-sm text-slate-500">No products found.</p>
                    )
                  ) : (
                    <p className="text-sm text-slate-500">Loading order products…</p>
                  )}
                </div>
              )}

              {(selectedTx?.category === "service" ||
                selectedTx?.category === "event" ||
                selectedTx?.relatedDetails) && (
                <RelatedPromotionDetails
                  details={selectedTx?.relatedDetails}
                  category={selectedTx?.category}
                />
              )}
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default AllTransactions;
